import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn';

// Button hierarchy (see docs/ui-guide.md):
//   primary          — the one main action of a screen/section (filled accent)
//   secondary        — alternative actions alongside a primary (outlined)
//   tertiary         — low-emphasis actions, esp. inline/table-row (text + hover surface)
//   tertiary-danger  — low-emphasis destructive (Void, Remove …)
//   ghost            — neutral utility actions (Cancel, toolbar toggles)
//   danger           — confirmed destructive actions (filled negative)
// Link styling is reserved for navigation — use <Link>/<BackLink>, never a button.
type Variant = 'primary' | 'secondary' | 'tertiary' | 'tertiary-danger' | 'ghost' | 'danger';
type Size = 'xs' | 'sm' | 'md' | 'lg';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  leadingIcon?: ReactNode;
}

// Every variant carries a visible resting background so a control always reads
// unambiguously as a button — never as plain text. Emphasis descends
// primary → secondary → tertiary → ghost; danger / tertiary-danger for destructive.
const VARIANTS: Record<Variant, string> = {
  primary: 'bg-accent text-white hover:bg-accent-2 disabled:bg-ink-4',
  secondary:
    'bg-surface border border-rule text-ink hover:bg-paper-2 hover:border-rule-2 disabled:opacity-60',
  tertiary: 'bg-accent-soft text-accent hover:brightness-95 disabled:opacity-50',
  'tertiary-danger': 'bg-negative-soft text-negative hover:brightness-95 disabled:opacity-50',
  ghost:
    'bg-paper-2 text-ink-2 border border-rule hover:bg-paper hover:text-ink disabled:opacity-60',
  danger: 'bg-negative text-white hover:bg-negative/90 disabled:opacity-60',
};
const SIZES: Record<Size, string> = {
  xs: 'h-7 px-2 text-xs',
  sm: 'h-8 px-3 text-xs',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-6 text-sm',
};

export function Button({
  variant = 'primary',
  size = 'md',
  loading,
  leadingIcon,
  className,
  children,
  disabled,
  ...rest
}: Props): JSX.Element {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded font-medium transition select-none whitespace-nowrap',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-soft',
        'disabled:cursor-not-allowed',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
    >
      {loading && (
        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeOpacity="0.25"
            strokeWidth="3"
          />
          <path
            d="M22 12a10 10 0 0 1-10 10"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
          />
        </svg>
      )}
      {!loading && leadingIcon}
      {children}
    </button>
  );
}
